import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";

export const LoadingContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(20, 0, 20, 0),
  backgroundColor: theme.palette.common.white,
  height: "100%",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
})) as typeof Box;

export const StyledCarouselContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  flex: 1,
  height: "100vh",
  width: "100%vw",
}));

export const LeoCardSkeletonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minWidth: theme.spacing(25),
  height: theme.spacing(56.375),
  [theme.breakpoints.down("sm")]: {
    minWidth: theme.spacing(20),
    height: theme.spacing(47.5),
  },
  borderRadius: theme.spacing(2),
}));

export const SkeletonImageContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: theme.spacing(25),
  "&.compact": {
    height: theme.spacing(18),
    flexShrink: 0,
    border: `${theme.spacing(0.125)} solid ${theme.palette.grey[100]}`,
    borderRadius: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    height: theme.spacing(20),
  },
}));

interface ProductListItemSkeletonWrapperProps {
  showMargin?: boolean;
}

export const ProductListItemSkeletonWrapper = styled(
  Box,
)<ProductListItemSkeletonWrapperProps>(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  flex: 1,
  backgroundColor: theme.palette.common.white,
}));

export const ImageContainer = styled("span")(({ theme }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const SkeletonContentContainer = styled(Box)(() => ({
  width: "100%",
  flexGrow: "1",
  overflow: "hidden",
}));

export const SkeletonWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.2),
  paddingRight: 0,
  paddingBottom: theme.spacing(1.2),
  marginLeft: 0,
}));

export const SkeletonRectLeo = styled("rect")(({ theme }) => ({
  x: theme.spacing(1),
  rx: theme.spacing(0.5),
  ry: theme.spacing(0.5),
  width: `calc(100% - ${theme.spacing(2)})`,
  y: theme.spacing(1),
  height: theme.spacing(2.5),
  "&.compact": {
    display: "none",
  },
  "&.first": {
    width: theme.spacing(7.625),
  },
  "&.second": {
    y: theme.spacing(5),
    height: theme.spacing(6),
  },
  "&.third": {
    y: theme.spacing(12),
    height: theme.spacing(5),
  },
  "&.fourth": {
    y: theme.spacing(18.5),
    height: theme.spacing(12),
  },
  "&.first.compact": {
    display: "block",
    x: theme.spacing(1),
    height: theme.spacing(6),
    width: `calc(100% - ${theme.spacing(2)})`,
  },
  "&.second.compact": {
    display: "block",
    x: theme.spacing(1),
    y: theme.spacing(7.5),
    width: theme.spacing(12.125),
    height: theme.spacing(4.125),
  },
}));

export const SkeletonWapper = styled(Box)(({ theme }) => ({
  flexDirection: "column",
  "&.isPlp": {
    background: theme.palette.grey[50],
  },
}));

export const StyledListHeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  height: theme.spacing(15),
  width: "100%vw",
}));

export const StyledSkeletonContainer = styled(Box)(({}) => ({
  width: "100%",
}));

export const StyledTitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

export const StyledTitleText = styled(Skeleton)(({ theme }) => ({
  width: theme.spacing(20),
  height: theme.spacing(3.5),
  borderRadius: theme.shape.borderRadius,
}));

export const StyledProductsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  overflowX: "auto",
  paddingBottom: theme.spacing(1),
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
}));

export const StyledProductCard = styled(Box)(({ theme }) => ({
  minWidth: "120px",
  maxHeight: "220px",
  flexShrink: 0,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
}));

export const StyledProductImage = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  height: theme.spacing(14),
  borderRadius: theme.shape.borderRadius,
}));

export const StyledProductName = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  height: theme.spacing(2.5),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export const StyledProductDescription = styled(Skeleton)(({ theme }) => ({
  width: "75%",
  height: theme.spacing(2),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export const StyledProductPieces = styled(Skeleton)(({ theme }) => ({
  width: "33%",
  height: theme.spacing(2),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export const StyledProductPrice = styled(Skeleton)(({ theme }) => ({
  width: "66%",
  height: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
}));

export const StyledTitleIcon = styled(Skeleton)(({ theme }) => ({
  width: theme.spacing(3),
  height: theme.spacing(5),
  borderRadius: "50%",
  aspectRatio: "1/1",
  flexShrink: 0,
}));

export const StyledAddButton = styled(Skeleton)(({ theme }) => ({
  width: theme.spacing(3),
  height: theme.spacing(5),
  borderRadius: "50%",
  position: "absolute",
  bottom: theme.spacing(2.5),
  right: theme.spacing(1),
  aspectRatio: "1/1",
  flexShrink: 0,
}));
